/**
 * TourSlide
 *
 * Parchment-styled content card that matches the TownCrier aesthetic.
 * Shows title, description, optional screenshot, and navigation controls.
 */

import { GouldCorner, SwordDivider, MachinaCenterpiece } from '../Ornaments';
import type { TourStep } from './tourSteps';

const GOLD = '#d4af37';
const PARCHMENT = '#f4e4bc';
const PARCHMENT_DARK = '#d4c4a0';
const INK = '#2a1810';

interface TourSlideProps {
  step: TourStep;
  stepIndex: number;
  totalSteps: number;
  isFirst: boolean;
  isLast: boolean;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  /** Where to position the slide relative to the spotlight */
  spotlightSelector?: string;
}

export function TourSlide({
  step,
  stepIndex,
  totalSteps,
  isFirst,
  isLast,
  onNext,
  onPrev,
  onSkip,
  spotlightSelector,
}: TourSlideProps) {
  // Determine positioning: if there's a spotlight, position beside it; otherwise center
  const hasSpotlight = !!spotlightSelector;
  const spotlightEl = hasSpotlight ? document.querySelector(spotlightSelector!) : null;
  const spotlightRect = spotlightEl?.getBoundingClientRect();

  // Compute slide position relative to spotlight
  let slideStyle: React.CSSProperties;
  if (spotlightRect) {
    const viewportWidth = window.innerWidth;
    const slideWidth = 420;

    // Place slide to the right of the spotlight if there's room, otherwise to the left
    const rightSpace = viewportWidth - spotlightRect.right;
    const leftSpace = spotlightRect.left;

    if (rightSpace > slideWidth + 40) {
      slideStyle = {
        position: 'fixed',
        top: Math.max(20, Math.min(spotlightRect.top, window.innerHeight - 400)),
        left: spotlightRect.right + 24,
        zIndex: 2501,
      };
    } else if (leftSpace > slideWidth + 40) {
      slideStyle = {
        position: 'fixed',
        top: Math.max(20, Math.min(spotlightRect.top, window.innerHeight - 400)),
        right: viewportWidth - spotlightRect.left + 24,
        zIndex: 2501,
      };
    } else {
      // Fallback: center below
      slideStyle = {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 2501,
      };
    }
  } else {
    // No spotlight — center on screen
    slideStyle = {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 2501,
    };
  }

  const isWelcome = step.id === 'welcome';

  return (
    <div
      style={slideStyle}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        style={{
          position: 'relative',
          width: 420,
          maxWidth: '90vw',
          background: `linear-gradient(180deg, ${PARCHMENT} 0%, ${PARCHMENT_DARK} 100%)`,
          borderRadius: 8,
          padding: '28px 24px 20px',
          boxShadow: `
            0 20px 60px rgba(0, 0, 0, 0.5),
            0 0 40px rgba(212, 175, 55, 0.2),
            inset 0 0 60px rgba(42, 24, 16, 0.1)
          `,
          border: `3px solid ${GOLD}`,
          animation: 'tourSlideEnter 300ms ease-out',
        }}
      >
        {/* Corner ornaments */}
        <GouldCorner size={28} color={GOLD} style={{ position: 'absolute', top: 6, left: 6 }} />
        <GouldCorner size={28} color={GOLD} style={{ position: 'absolute', top: 6, right: 6, transform: 'scaleX(-1)' }} />
        <GouldCorner size={28} color={GOLD} style={{ position: 'absolute', bottom: 6, left: 6, transform: 'scaleY(-1)' }} />
        <GouldCorner size={28} color={GOLD} style={{ position: 'absolute', bottom: 6, right: 6, transform: 'scale(-1)' }} />

        {/* Welcome step gets the Machina centerpiece */}
        {isWelcome && (
          <div style={{ textAlign: 'center', marginBottom: 12 }}>
            <MachinaCenterpiece size={200} />
          </div>
        )}

        {/* Title */}
        <h2 style={{
          margin: '0 0 8px 0',
          fontSize: 22,
          fontFamily: 'var(--font-display)',
          color: INK,
          fontWeight: 400,
          letterSpacing: 1,
          textAlign: 'center',
        }}>
          {step.title}
        </h2>

        {/* Divider */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
          <SwordDivider color={GOLD} size={180} />
        </div>

        {/* Screenshot image */}
        {step.image && (
          <div style={{
            marginBottom: 14,
            borderRadius: 4,
            border: `2px solid ${GOLD}`,
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          }}>
            <img
              src={step.image}
              alt={step.title}
              style={{
                width: '100%',
                height: 160,
                objectFit: 'cover',
                display: 'block',
              }}
              onError={(e) => {
                // Hide broken image — the placeholder border still shows nicely
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
        )}

        {/* Description */}
        <p style={{
          margin: '0 0 18px 0',
          fontSize: 14,
          fontFamily: 'var(--font-body)',
          color: INK,
          lineHeight: 1.6,
          textAlign: 'center',
          opacity: 0.85,
        }}>
          {step.description}
        </p>

        {/* Navigation */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          {/* Skip */}
          <button
            onClick={onSkip}
            style={{
              background: 'none',
              border: 'none',
              color: INK,
              opacity: 0.5,
              fontSize: 12,
              fontFamily: 'var(--font-display)',
              cursor: 'pointer',
              padding: '4px 8px',
              letterSpacing: '0.05em',
            }}
          >
            Skip Tour
          </button>

          {/* Step dots */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                style={{
                  width: i === stepIndex ? 10 : 6,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: i === stepIndex ? GOLD : `${INK}30`,
                  transition: 'all 200ms ease',
                }}
              />
            ))}
          </div>

          {/* Prev / Next */}
          <div style={{ display: 'flex', gap: 6 }}>
            {!isFirst && (
              <button
                onClick={onPrev}
                style={{
                  padding: '6px 14px',
                  fontSize: 12,
                  fontFamily: 'var(--font-display)',
                  fontWeight: 600,
                  background: 'transparent',
                  border: `1px solid ${GOLD}`,
                  borderRadius: 4,
                  color: INK,
                  cursor: 'pointer',
                  letterSpacing: '0.05em',
                }}
              >
                Prev
              </button>
            )}
            <button
              onClick={onNext}
              style={{
                padding: '6px 16px',
                fontSize: 12,
                fontFamily: 'var(--font-display)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                background: isLast
                  ? `linear-gradient(180deg, ${GOLD} 0%, #a08030 50%, #8b6914 100%)`
                  : `linear-gradient(180deg, ${GOLD} 0%, #a08030 100%)`,
                border: `2px solid ${GOLD}`,
                borderRadius: 4,
                color: INK,
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
              }}
            >
              {isLast ? 'Begin Your Adventure' : 'Next'}
            </button>
          </div>
        </div>

        {/* Animation keyframes */}
        <style>{`
          @keyframes tourSlideEnter {
            from {
              opacity: 0;
              transform: scale(0.92) translateY(12px);
            }
            to {
              opacity: 1;
              transform: scale(1) translateY(0);
            }
          }
        `}</style>
      </div>
    </div>
  );
}
